<script>
    import { onMount, afterUpdate } from "svelte";
    import { Chart } from "chart.js/auto";
    import { selectedItem } from "../../store/store.js";
    import {
        displayInt,
        tooltip,
        calculateNettoskuld,
        safeDivide,
        safeMultiply,
    } from "../../utils/utils.js";
    import DoughnutChart from "../common/DoughnutChart.svelte";

    let chartCanvas;
    let chartInstance = null;
    let historicalData = {};

    $: if (
        $selectedItem &&
        $selectedItem.core_history &&
        $selectedItem.core_history.history_by_year
    ) {
        const history = $selectedItem.core_history.history_by_year;
        const years = Object.keys(history).sort();

        historicalData = {
            years,
            revenue: years.map((year) =>
                (history[year].net_revenue / 1000000).toFixed(1),
            ),
            debt_per_sqm: years.map(
                (year) => history[year].loans_per_living_sqm,
            ),
            total_debt: years.map(
                (year) => history[year].total_amount_of_loans || 0,
            ),
        };

        if (chartCanvas && years.length > 2) {
            renderChart();
        }
    }

    let incomeDistribution = [];
    $: if ($selectedItem) {
        const annualFeeIncome =
            $selectedItem.annual_fee_income != null
                ? $selectedItem.annual_fee_income
                : $selectedItem.living_fee_per_sqm != null &&
                    $selectedItem.living_area_sqm != null
                  ? Math.round(
                        $selectedItem.living_fee_per_sqm *
                            $selectedItem.living_area_sqm,
                    )
                  : 0;

        const totalIncome =
            annualFeeIncome +
            ($selectedItem.rental_income_from_commercial_premises || 0) +
            ($selectedItem.rental_income_from_rental_apartments || 0) +
            ($selectedItem.rental_income_from_parking_spots || 0) +
            ($selectedItem.other_revenues || 0);

        if (totalIncome > 0) {
            incomeDistribution = [
                {
                    name: "Avgifter",
                    value: annualFeeIncome,
                    percentage: Math.round(
                        (annualFeeIncome / totalIncome) * 100,
                    ),
                },
                {
                    name: "Lokaler",
                    value: $selectedItem.rental_income_from_commercial_premises,
                    percentage: Math.round(
                        ($selectedItem.rental_income_from_commercial_premises /
                            totalIncome) *
                            100,
                    ),
                },
                {
                    name: "Hyresrätter",
                    value: $selectedItem.rental_income_from_rental_apartments,
                    percentage: Math.round(
                        ($selectedItem.rental_income_from_rental_apartments /
                            totalIncome) *
                            100,
                    ),
                },
                {
                    name: "Parkering",
                    value: $selectedItem.rental_income_from_parking_spots,
                    percentage: Math.round(
                        ($selectedItem.rental_income_from_parking_spots /
                            totalIncome) *
                            100,
                    ),
                },
                {
                    name: "Övrigt",
                    value: $selectedItem.other_revenues,
                    percentage: Math.round(
                        ($selectedItem.other_revenues / totalIncome) * 100,
                    ),
                },
            ].filter((item) => item.value > 0);
        } else {
            incomeDistribution = [];
        }
    }

    let businessDistribution = [];
    $: if ($selectedItem && $selectedItem.business_distribution) {
        try {
            const data =
                typeof $selectedItem.business_distribution === "string"
                    ? JSON.parse($selectedItem.business_distribution)
                    : $selectedItem.business_distribution;

            if (Array.isArray(data)) {
                businessDistribution = data
                    .map((item) => ({
                        name: item.business_name,
                        value: item.percentage_of_atemp,
                    }))
                    .filter((item) => item.value);
            } else {
                businessDistribution = [];
            }
        } catch (e) {
            console.error("Error parsing business_distribution:", e);
            businessDistribution = [];
        }
    }

    let businessDistributionChartData;
    $: if (businessDistribution.length > 0) {
        const backgroundColors = [
            "#4361ee",
            "#A9B9E8",
            "#849DE0",
            "#eef2ff",
            "#5a5a5b",
            "#9b59b6",
            "#3498db",
            "#95a5a6",
            "#e74c3c",
            "#34495e",
        ];
        businessDistributionChartData = {
            labels: businessDistribution.map((item) => item.name),
            datasets: [
                {
                    data: businessDistribution.map((item) => item.value),
                    backgroundColor: backgroundColors.slice(
                        0,
                        businessDistribution.length,
                    ),
                    hoverOffset: 4,
                    borderWidth: 0,
                },
            ],
        };
    }

    afterUpdate(() => {
        if (chartCanvas && !chartInstance && historicalData.years?.length > 2) {
            renderChart();
        }
    });

    onMount(() => {
        return () => {
            if (chartInstance) {
                chartInstance.destroy();
                chartInstance = null;
            }
        };
    });

    function renderChart() {
        if (chartInstance) {
            chartInstance.destroy();
        }
        if (!chartCanvas) return;
        const ctx = chartCanvas.getContext("2d");

        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, "rgba(210, 220, 255, 0.8)");
        gradient.addColorStop(1, "rgba(210, 220, 255, 0)");

        chartInstance = new Chart(chartCanvas, {
            type: "bar",
            data: {
                labels: historicalData.years,
                datasets: [
                    {
                        type: "line",
                        label: "Omsättning (Mkr)",
                        data: historicalData.revenue,
                        borderColor: "#4361ee",
                        backgroundColor: gradient,
                        fill: true,
                        tension: 0.4,
                        yAxisID: "y1",
                        borderWidth: 2,
                    },
                    {
                        label: "Skuld/kvm",
                        data: historicalData.debt_per_sqm,
                        backgroundColor: "#A9B9E8",
                        borderColor: "#849DE0",
                        barThickness: 20,
                        borderRadius: 4,
                        yAxisID: "y",
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: { color: "#888", font: { size: 12 } },
                    },
                    y: {
                        position: "left",
                        beginAtZero: false,
                        grid: {
                            color: "#eee",
                            borderDash: [5, 5],
                        },
                        ticks: {
                            color: "#888",
                            font: { size: 11 },
                            callback: function (value) {
                                return displayInt(value) + " kr";
                            },
                        },
                        title: {
                            display: true,
                            text: "Skuld/kvm (kr)",
                            color: "#666",
                            font: { size: 12, weight: "500" },
                        },
                    },
                    y1: {
                        position: "right",
                        beginAtZero: true,
                        grid: { display: false },
                        ticks: {
                            color: "#888",
                            font: { size: 11 },
                        },
                        title: {
                            display: true,
                            text: "Omsättning (Mkr)",
                            color: "#666",
                            font: { size: 12, weight: "500" },
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        padding: 12,
                        titleFont: { size: 13 },
                        bodyFont: { size: 12 },
                        borderColor: "#4361ee",
                        borderWidth: 1,
                        cornerRadius: 8,
                    },
                },
                interaction: { intersect: false, mode: "index" },
            },
        });
    }

    function toTitleCase(str) {
        if (!str) return "Uppgifter saknas";
        return str
            .toLowerCase()
            .split(" ")
            .map((word) => {
                if (word.length === 0) return "";
                return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(" ");
    }

    function displayAnnualFeeIncome(itemData) {
        if (itemData.annual_fee_income != null) {
            return displayInt(itemData.annual_fee_income);
        } else if (
            itemData.living_fee_per_sqm != null &&
            itemData.living_area_sqm != null
        ) {
            return displayInt(
                Math.round(
                    itemData.living_fee_per_sqm * itemData.living_area_sqm,
                ),
            );
        }
        return "0";
    }

    function isLoanExpired(expirationDate) {
        if (!expirationDate) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiry = new Date(expirationDate);
        expiry.setHours(0, 0, 0, 0);
        return expiry < today;
    }

    $: expiredLoans =
        $selectedItem?.loans?.filter((loan) =>
            isLoanExpired(loan.expiration_date),
        ) || [];

    let loanTotal = 0;
    let loanAverageRate = null;

    $: if ($selectedItem?.loans && $selectedItem.loans.length > 0) {
        const validLoans = $selectedItem.loans.filter(
            (loan) => loan.amount != null && loan.amount > 0,
        );
        loanTotal = validLoans.reduce(
            (sum, loan) => sum + (loan.amount || 0),
            0,
        );

        const loansWithRate = validLoans.filter(
            (loan) => loan.rate_percentage != null && loan.rate_percentage >= 0,
        );
        if (loansWithRate.length > 0 && loanTotal > 0) {
            const weightedSum = loansWithRate.reduce(
                (sum, loan) => sum + loan.amount * loan.rate_percentage,
                0,
            );
            loanAverageRate = weightedSum / loanTotal;
        } else {
            loanAverageRate = null;
        }
    } else {
        loanTotal = 0;
        loanAverageRate = null;
    }
</script>

<div class="key-value-table box">
    <div class="header-card">
        <h5>
            <i class="fa-solid fa-chart-line fa-sm icon-detail"></i> Ekonomi & Lån
        </h5>
    </div>

    {#if historicalData.years?.length > 2}
        <div class="chart-section">
            <div class="card chart-card">
                <h6 class="section-title">
                    <i class="fas fa-chart-area"></i>
                    Finansiell Utveckling
                </h6>
                <div class="chart-wrapper">
                    <canvas bind:this={chartCanvas}></canvas>
                </div>
            </div>
        </div>
    {/if}

    <div class="sections-container">
        <div class="section">
            <h6 class="section-title">Lån & Skuldsättning</h6>
            <div class="metrics-grid">
                {#if $selectedItem.loans_per_living_sqm != null}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Lån per bostadsyta</h6>
                            <div class="tooltip-container" use:tooltip>
                                <span class="info-icon"></span>
                                <div class="tooltip-content">
                                    <p>
                                        Denna siffra baseras på föreningens
                                        totala lån och den totala boytan.
                                    </p>
                                    <p><strong>Riktlinjer:</strong></p>
                                    <ul>
                                        <li>
                                            Låga lån per kvm (under 5 000
                                            SEK/kvm) är mest eftertraktat för
                                            stabilitet.
                                        </li>
                                        <li>
                                            Måttliga lån per kvm (5 000 - 10 000
                                            SEK/kvm) kan anses rimliga beroende
                                            på föreningens planerade underhåll.
                                        </li>
                                        <li>
                                            Höga lån per kvm (över 10 000
                                            SEK/kvm) ökar risken för framtida
                                            avgiftshöjningar.
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.loans_per_living_sqm)} kr/m²
                        </div>
                        {#if historicalData.debt_per_sqm?.length > 1}
                            {@const recent =
                                historicalData.debt_per_sqm.slice(-2)}
                            {@const trend =
                                recent[1] > recent[0]
                                    ? "up"
                                    : recent[1] < recent[0]
                                      ? "down"
                                      : "stable"}
                            <div class="metric-subvalue">
                                {trend === "up"
                                    ? "↑ Ökning"
                                    : trend === "down"
                                      ? "↓ Minskning"
                                      : "→ Stabilt"}
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.total_amount_of_loans != null}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Total summa lån</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.total_amount_of_loans)} kr
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    displayInt(
                                        $selectedItem.total_amount_of_loans,
                                    ).replace(/\s/g, ""),
                                    $selectedItem.living_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.rate_sensitivity != null}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Räntekänslighet</h6>
                            <div class="tooltip-container" use:tooltip>
                                <span class="info-icon"></span>
                                <div class="tooltip-content">
                                    <p>
                                        Visar hur mycket årsavgiften (i procent)
                                        behöver höjas om den genomsnittliga
                                        räntan på föreningens lån ökar med 1
                                        procentenhet.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="metric-value">
                            {$selectedItem.rate_sensitivity} %
                        </div>
                    </div>
                {/if}

                {#if $selectedItem.total_amount_of_loans != null}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Indirekt Nettoskuldsättning</h6>
                            <div class="tooltip-container" use:tooltip>
                                <span class="info-icon"></span>
                                <div class="tooltip-content">
                                    <p>
                                        Föreningens räntebärande skulder minus
                                        räntebärande tillgångar och likvida
                                        medel. Ger en bild av den faktiska
                                        skulden.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="metric-value">
                            {displayInt(
                                calculateNettoskuld(
                                    $selectedItem.total_amount_of_loans,
                                    $selectedItem.cash_at_the_end_of_the_year,
                                    $selectedItem.skattekonto,
                                    $selectedItem.klientmedel,
                                    $selectedItem.kassa_och_bank,
                                ),
                            )} kr
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            {@const nettoSkuld = calculateNettoskuld(
                                $selectedItem.total_amount_of_loans,
                                $selectedItem.cash_at_the_end_of_the_year,
                                $selectedItem.skattekonto,
                                $selectedItem.klientmedel,
                                $selectedItem.kassa_och_bank,
                            )}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    nettoSkuld,
                                    $selectedItem.living_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        </div>

        <div class="section">
            <h6 class="section-title">Ekonomiska Resultat</h6>
            <div class="metrics-grid">
                {#if $selectedItem.living_fee_per_sqm != null}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Avgift per kvm</h6>
                            <div class="tooltip-container" use:tooltip>
                                <span class="info-icon"></span>
                                <div class="tooltip-content">
                                    <p>
                                        Månadsavgift per kvadratmeter. Låg
                                        avgift (under 600 SEK/kvm) indikerar
                                        ofta välskött förening eller andra
                                        inkomstkällor.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.living_fee_per_sqm)} kr/m²
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeMultiply(
                                    displayInt(
                                        $selectedItem.living_fee_per_sqm,
                                    ).replace(/\s/g, ""),
                                    $selectedItem.living_area_sqm,
                                    "kr",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.saving_per_sqm != null}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Sparande per kvm</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.saving_per_sqm)} kr/m²
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeMultiply(
                                    displayInt(
                                        $selectedItem.saving_per_sqm,
                                    ).replace(/\s/g, ""),
                                    $selectedItem.living_area_sqm,
                                    "kr",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.equity_ratio != null}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Soliditet</h6>
                            <div class="tooltip-container" use:tooltip>
                                <span class="info-icon"></span>
                                <div class="tooltip-content">
                                    <p>
                                        Visar hur stor andel av föreningens
                                        totala tillgångar som finansieras med
                                        eget kapital. Ett mått på finansiell
                                        stabilitet.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.equity_ratio)} %
                        </div>
                    </div>
                {/if}

                {#if $selectedItem.ebit != null}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Rörelseresultat</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.ebit)} kr
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    displayInt($selectedItem.ebit).replace(
                                        /\s/g,
                                        "",
                                    ),
                                    $selectedItem.living_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        </div>
    </div>

    <div class="sections-container">
        <div class="section">
            <h6 class="section-title">Intäkter</h6>
            <div class="metrics-grid">
                {#if $selectedItem.annual_fee_income != null || $selectedItem.living_fee_per_sqm != null}
                    {@const annualFee = displayAnnualFeeIncome($selectedItem)}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Årlig avgiftsinkomst</h6>
                        </div>
                        <div class="metric-value">
                            {annualFee} kr
                        </div>
                        {#if $selectedItem.living_area_sqm && annualFee !== "0"}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    annualFee.replace(/\s/g, ""),
                                    $selectedItem.living_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.rental_income_from_commercial_premises != null}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Lokalintäkter</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt(
                                $selectedItem.rental_income_from_commercial_premises,
                            )} kr
                        </div>
                        {#if $selectedItem.commercial_area_sqm}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    displayInt(
                                        $selectedItem.rental_income_from_commercial_premises,
                                    ).replace(/\s/g, ""),
                                    $selectedItem.commercial_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.other_revenues != null}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Andra intäkter</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.other_revenues)} kr
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    displayInt(
                                        $selectedItem.other_revenues,
                                    ).replace(/\s/g, ""),
                                    $selectedItem.living_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        </div>

        <div class="section">
            <h6 class="section-title">
                Intäktsfördelning ({$selectedItem?.year || "N/A"})
            </h6>
            {#if incomeDistribution.length > 0}
                <div class="income-list">
                    {#each incomeDistribution as item}
                        <div class="income-item">
                            <div class="income-header">
                                <span class="income-name">{item.name}</span>
                                <span class="income-percentage"
                                    >{item.percentage}%</span
                                >
                            </div>
                            <div class="progress-bar-container">
                                <div
                                    class="progress-bar"
                                    style="width: {item.percentage}%"
                                ></div>
                            </div>
                            <div class="income-amount">
                                {displayInt(item.value)} kr
                            </div>
                        </div>
                    {/each}
                </div>
            {:else}
                <p class="no-data">Ingen intäktsdata tillgänglig.</p>
            {/if}
        </div>
    </div>

    {#if businessDistribution.length > 0 && businessDistributionChartData}
        <DoughnutChart
            headerTitle="Lokalfördelning"
            chartData={businessDistributionChartData}
        />
    {/if}

    {#if $selectedItem?.loans && $selectedItem.loans.length > 0}
        <div class="section loans-detail-section">
            <h6 class="section-title">Låneinformation</h6>
            {#if expiredLoans.length > 0}
                <div class="expired-loans-notice">
                    <i class="fas fa-exclamation-circle"></i>
                    <span
                        >{expiredLoans.length === 1
                            ? "Ett lån har"
                            : expiredLoans.length + " lån har"} utgått. Kontrollera
                        låneinformationen med föreningen för att få den senaste informationen.</span
                    >
                </div>
            {/if}
            <div class="loan-table-container">
                <table class="loan-table">
                    <thead>
                        <tr>
                            <th class="bank-column">Bank</th>
                            <th>Summa</th>
                            <th>Ränta</th>
                            <th>Utgångsdatum</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each $selectedItem.loans
                            .slice()
                            .sort((a, b) => new Date(a.expiration_date || 0) - new Date(b.expiration_date || 0)) as loan}
                            {@const isExpired = isLoanExpired(
                                loan.expiration_date,
                            )}
                            <tr class:expired={isExpired}>
                                <td class="bank-column">
                                    {#if isExpired}
                                        <span
                                            class="expired-indicator"
                                            title="Detta lån har utgått"
                                        >
                                            <i class="fas fa-exclamation-circle"
                                            ></i>
                                        </span>
                                    {/if}
                                    {loan.bank_name
                                        ? toTitleCase(loan.bank_name)
                                        : "Uppgifter saknas"}
                                </td>
                                <td>{displayInt(loan.amount)} kr</td>
                                <td
                                    >{loan?.rate_percentage != null
                                        ? loan?.rate_percentage?.toLocaleString(
                                              "sv-SE",
                                              {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                              },
                                          ) + " %"
                                        : "Uppgifter saknas"}</td
                                >
                                <td
                                    class="expiration-date"
                                    class:expired={isExpired}
                                >
                                    {#if isExpired}
                                        <span class="expired-badge"
                                            >Utgånget</span
                                        >
                                    {/if}
                                    {loan.expiration_date
                                        ? loan.expiration_date
                                        : "Uppgifter saknas"}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                    <tfoot>
                        <tr class="summary-row">
                            <td class="bank-column">
                                <strong>Totalt</strong>
                                <div class="tooltip-container" use:tooltip>
                                    <span class="info-icon"></span>
                                    <div class="tooltip-content">
                                        <p>
                                            <strong>Totalbelopp:</strong> Summan
                                            av alla lån i föreningen.
                                        </p>
                                        <br />
                                        <p>
                                            <strong>Genomsnittsränta:</strong> Viktad
                                            genomsnittsränta baserad på lånebeloppen.
                                            Beräknas som (summa av belopp × ränta)
                                            / totalbelopp.
                                        </p>
                                        <p>
                                            Exempel: 1% på 1 000 000 kr och 2%
                                            på 2 000 000 kr ger
                                            genomsnittsräntan 1,67%.
                                        </p>
                                        <p>
                                            En låg genomsnittsränta indikerar
                                            ofta bättre finansieringsvillkor och
                                            lägre kostnader för föreningen.
                                        </p>
                                    </div>
                                </div>
                            </td>
                            <td><strong>{displayInt(loanTotal)} kr</strong></td>
                            <td>
                                <strong>
                                    {#if loanAverageRate != null}
                                        {loanAverageRate.toLocaleString(
                                            "sv-SE",
                                            {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            },
                                        )} %
                                    {:else}
                                        Uppgifter saknas
                                    {/if}
                                </strong>
                            </td>
                            <td></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    {/if}
</div>

<style>
    .sections-container {
        display: flex;
        gap: 24px;
        margin-bottom: 24px;
    }

    .section {
        flex: 1;
        min-width: 0;
    }

    .loans-detail-section {
        width: 100%;
    }

    .chart-section {
        margin-bottom: 24px;
    }

    .chart-card {
        background-color: #ffffff;
        border-radius: 8px;
        padding: 1.5rem;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .chart-wrapper {
        height: 400px;
    }

    .section-title {
        font-size: 1rem;
        color: #495057;
        margin-bottom: 12px;
        padding-bottom: 6px;
        border-bottom: 1px solid #e9ecef;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .section-title i {
        color: #4361ee;
    }

    .income-list {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
    }

    .income-item {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .income-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .income-name {
        font-weight: 500;
        font-size: 0.95rem;
        color: #333;
    }

    .income-percentage {
        font-weight: 600;
        font-size: 0.95rem;
        color: #4361ee;
    }

    .progress-bar-container {
        background-color: #e9ecef;
        border-radius: 8px;
        height: 10px;
        overflow: hidden;
    }

    .progress-bar {
        background: linear-gradient(90deg, #4361ee 0%, #5a7cff 100%);
        height: 100%;
        border-radius: 8px;
        transition: width 0.3s ease;
    }

    .income-amount {
        font-size: 0.85rem;
        color: #888;
        margin-top: -0.25rem;
    }

    .no-data {
        text-align: center;
        color: #6c757d;
        padding: 2rem;
        font-style: italic;
    }

    .loan-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.9rem;
    }

    .loan-table th,
    .loan-table td {
        padding: 10px;
        text-align: left;
        border-bottom: 1px solid #e9ecef;
        vertical-align: middle;
    }

    .loan-table th {
        font-weight: 600;
        color: #495057;
        background-color: #f8f9fa;
    }

    .loan-table tr:last-child td {
        border-bottom: none;
    }

    .loan-table tfoot .summary-row {
        background-color: #f8f9fa;
        border-top: 2px solid #4361ee;
    }

    .loan-table tfoot .summary-row td {
        padding: 12px 10px;
        font-weight: 600;
    }

    .loan-table tfoot .summary-row td .tooltip-container {
        display: inline-block;
        margin-left: 8px;
        vertical-align: middle;
    }

    .loan-table tfoot .summary-row td .tooltip-container .info-icon {
        cursor: help;
    }

    .loan-table tr.expired {
        background-color: #fff8e1;
        border-left: 3px solid #ffa726;
    }

    .loan-table tr.expired:hover {
        background-color: #ffecb3;
    }

    .expired-indicator {
        color: #ffa726;
        margin-right: 6px;
        font-size: 0.9rem;
    }

    .expiration-date {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .expiration-date.expired {
        color: #e65100;
    }

    .expired-badge {
        display: inline-block;
        background-color: #ffa726;
        color: #fff;
        font-size: 0.75rem;
        font-weight: 600;
        padding: 2px 8px;
        border-radius: 4px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .expired-loans-notice {
        display: flex;
        align-items: center;
        gap: 8px;
        background-color: #fff3cd;
        border: 1px solid #ffc107;
        border-radius: 6px;
        padding: 12px 16px;
        margin-bottom: 16px;
        color: #856404;
        font-size: 0.9rem;
    }

    .expired-loans-notice i {
        color: #ffa726;
        font-size: 1rem;
    }

    @media (max-width: 1024px) {
        .sections-container {
            flex-direction: column;
        }

        .loan-table .bank-column {
            display: none;
        }
    }

    @media (min-width: 768px) {
        .loan-table .bank-column {
            display: table-cell;
        }
    }

    @media (max-width: 768px) {
        .chart-wrapper {
            height: 300px;
        }

        .loan-table-container {
            overflow-x: auto;
        }

        .loan-table {
            font-size: 0.8rem;
        }

        .loan-table th,
        .loan-table td {
            padding: 0.5rem;
        }
    }
</style>
